(function () {
  const DIALOG_ID = 'gift-details-dialog';
  const GIFT_CARD_TAG = 'gift-card';
  // Popup is removed. If user tries to checkout from /cart, optionally send them to the wizard page.
  const CUSTOMIZE_CART_URL =
    (document.body && document.body.getAttribute('data-customize-cart-url')) || '/pages/customize-cart';
  const REDIRECT_DRAWER_TO_CUSTOMIZE_CART =
    (document.body && document.body.getAttribute('data-customize-cart-redirect-drawer')) === 'true';
  /** @type {Map<string, any|null>} */
  const productJsonCache = new Map();

  function getDialog() {
    // There are cases where this snippet is rendered more than once (e.g. inside the cart drawer
    // and also on the cart page). Duplicate IDs are invalid HTML, but can happen in themes.
    // In that case, `getElementById` may return a dialog nested inside a CLOSED drawer dialog,
    // which either won't open or will show only the backdrop/overlay.
    const all = Array.from(document.querySelectorAll(`#${DIALOG_ID}`)).filter(
      (el) => el instanceof HTMLDialogElement
    );

    if (all.length === 0) return null;
    if (all.length === 1) return all[0];

    // If the cart drawer dialog is open, prefer the dialog inside it.
    const openDrawerDialog = document.querySelector('cart-drawer-component dialog[open]');
    if (openDrawerDialog instanceof HTMLDialogElement) {
      const insideOpenDrawer = all.find((d) => openDrawerDialog.contains(d));
      if (insideOpenDrawer) return insideOpenDrawer;
    }

    // Otherwise prefer a dialog that is NOT nested inside another <dialog>.
    const notNested = all.find((d) => {
      const parent = d.parentElement;
      return !(parent && parent.closest('dialog'));
    });
    return notNested || all[0];
  }

  /**
   * Inline gift details container (used on customize cart).
   * @returns {HTMLElement|null}
   */
  function getInlineGiftDetailsRoot() {
    const el = document.querySelector('[data-gift-details-inline]');
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * Extract product handle from a cart item.
   * Prefers `item.handle` if present, otherwise parses `item.url`.
   * @param {any} item
   * @returns {string|null}
   */
  /**
   * @param {any} item
   * @returns {string|null}
   */
  function getHandleFromCartItem(item) {
    if (item && typeof item.handle === 'string' && item.handle.trim()) {
      return item.handle.trim();
    }

    const url = item && typeof item.url === 'string' ? item.url : '';
    if (!url) return null;

    // `item.url` can be like:
    // - /products/my-handle?variant=...
    // - /collections/xyz/products/my-handle?variant=...
    const path = url.split('?')[0];
    const marker = '/products/';
    const idx = path.indexOf(marker);
    if (idx === -1) return null;

    const after = path.slice(idx + marker.length);
    const handle = after.split('/')[0];
    return handle ? handle : null;
  }

  /**
   * Fetch Shopify product JSON by handle with caching.
   * @param {string} handle
   * @returns {Promise<any|null>}
   */
  /**
   * @param {string} handle
   * @returns {Promise<any|null>}
   */
  async function fetchProductJson(handle) {
    if (!handle) return null;

    if (productJsonCache.has(handle)) {
      return productJsonCache.get(handle);
    }

    const url = `/products/${encodeURIComponent(handle)}.js`;
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) {
        productJsonCache.set(handle, null);
        return null;
      }
      const product = await res.json();
      productJsonCache.set(handle, product);
      return product;
    } catch (err) {
      console.error('Error fetching product JSON:', err);
      productJsonCache.set(handle, null);
      return null;
    }
  }

  /**
   * Determine if a product JSON contains a tag.
   * Supports tags as array or comma-separated string.
   * @param {any} product
   * @param {string} tag
   * @returns {boolean}
   */
  /**
   * @param {any} product
   * @param {string} tag
   * @returns {boolean}
   */
  function productHasTag(product, tag) {
    if (!product || !tag) return false;

    const raw = product.tags;
    const target = String(tag).toLowerCase();

    if (Array.isArray(raw)) {
      return raw.some((t) => String(t).toLowerCase() === target);
    }

    if (typeof raw === 'string') {
      return raw
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean)
        .includes(target);
    }

    return false;
  }
  /**
   * Proceed with the platform's normal checkout behavior.
   * For /cart, Shopify uses a submit button (name="checkout") tied to the cart form.
   * We must include the submitter, so we use requestSubmit(submitter) when available.
   * @param {Element|null} checkoutLink
   * @param {Element|null} checkoutBtn
   */
  /**
   * @param {Element|null} checkoutLink
   * @param {Element|null} checkoutBtn
   */
  function proceedToCheckout(checkoutLink, checkoutBtn) {
    closeOpenCartDrawer();
    if (checkoutLink && checkoutLink instanceof HTMLAnchorElement && checkoutLink.href) {
      window.location.href = checkoutLink.href;
      return;
    }


    if (checkoutBtn && (checkoutBtn instanceof HTMLButtonElement || checkoutBtn instanceof HTMLInputElement)) {
      const form = checkoutBtn.form || null;
      if (form instanceof HTMLFormElement) {
        if (typeof form.requestSubmit === 'function') {
          try {
            form.requestSubmit(checkoutBtn);
            return;
          } catch (e) {
            // fall through
          }
        }

        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = checkoutBtn.getAttribute('name') || 'checkout';
        hidden.value = '1';
        form.appendChild(hidden);
        form.submit();
        hidden.remove();
        return;
      }

      window.location.href = '/checkout';
      return;
    }

    window.location.href = '/checkout';
  }

  function closeOpenCartDrawer() {
    const openDrawerDialog = document.querySelector('cart-drawer-component dialog[open]');
    if (openDrawerDialog instanceof HTMLDialogElement) {
      try {
        openDrawerDialog.close();
      } catch (_) {
        // ignore
      }
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
  }

  async function flushActiveDeliverySchedulingSave() {
    const schedulingEl = getActiveDeliverySchedulingEl();
    const schedulingApi = /** @type {any} */ (schedulingEl);
    if (schedulingApi && typeof schedulingApi.saveDeliveryData === 'function') {
      try {
        await schedulingApi.saveDeliveryData();
      } catch (_) {
        // ignore
      }
    }
  }

  /**
   * Check if cart contains any product with the gift-card tag
   * @returns {Promise<boolean>}
   */
  async function hasGiftCardProduct() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return false;
      
      const cart = await response.json();
      if (!cart || !cart.items || cart.items.length === 0) return false;

      // We can't rely on `item.product.tags` in cart.js for some themes.
      // Instead, fetch each product JSON using its handle/url and check tags.
      const checks = await Promise.all(
        cart.items.map(async (/** @type {any} */ item) => {
          const handle = getHandleFromCartItem(item);
          if (!handle) return false;
          const product = await fetchProductJson(handle);
          return productHasTag(product, GIFT_CARD_TAG);
        })
      );

      return checks.some(Boolean);
    } catch (error) {
      console.error('Error checking for gift-card products:', error);
      return false;
    }
  }

  /**
   * Check if gift details are already filled
   * @returns {Promise<boolean>}
   */
  async function hasGiftDetails() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return false;
      
      const cart = await response.json();
      if (!cart || !cart.attributes) return false;

      const attrs = cart.attributes;
      return !!(
        String(attrs.receiver_name || '').trim() &&
        String(attrs.receiver_phone || '').trim() &&
        String(attrs.receiver_address || '').trim()
      );
    } catch (error) {
      console.error('Error checking gift details:', error);
      return false;
    }
  }

  /**
   * Show loading state on save button
   * @param {Element} scope
   */
  function showLoading(scope) {
    const saveBtn = scope.querySelector('[data-gift-save]');
    if (saveBtn instanceof HTMLButtonElement || saveBtn instanceof HTMLInputElement) {
      saveBtn.setAttribute('data-loading', 'true');
      saveBtn.disabled = true;
    }
  }

  /**
   * Hide loading state on save button
   * @param {Element} scope
   */
  function hideLoading(scope) {
    const saveBtn = scope.querySelector('[data-gift-save]');
    if (saveBtn instanceof HTMLButtonElement || saveBtn instanceof HTMLInputElement) {
      saveBtn.removeAttribute('data-loading');
      saveBtn.disabled = false;
    }
  }

  /**
   * Add real-time validation feedback
   */
  /**
   * @param {Element} scope
   */
  function addRealTimeValidation(scope) {
    // Email validation
    const emailInputs = scope.querySelectorAll('input[type="email"]');
    emailInputs.forEach((/** @type {HTMLInputElement} */ input) => {
      input.addEventListener('blur', () => {
        const value = input.value.trim();
        if (value && !isValidEmail(value)) {
          input.setCustomValidity('Please enter a valid email address');
          input.classList.add('gift-field__input--error');
        } else {
          input.setCustomValidity('');
          input.classList.remove('gift-field__input--error');
        }
      });

      input.addEventListener('input', () => {
        if (input.classList.contains('gift-field__input--error')) {
          const value = input.value.trim();
          if (!value || isValidEmail(value)) {
            input.setCustomValidity('');
            input.classList.remove('gift-field__input--error');
          }
        }
      });
    });

    // Phone validation
    const phoneInputs = scope.querySelectorAll('input[type="tel"]');
    phoneInputs.forEach((/** @type {HTMLInputElement} */ input) => {
      input.addEventListener('blur', () => {
        const value = input.value.trim();
        if (value && !isValidPhone(value)) {
          input.setCustomValidity('Phone number must start with a country code (e.g., +961)');
          input.classList.add('gift-field__input--error');
        } else {
          input.setCustomValidity('');
          input.classList.remove('gift-field__input--error');
        }
      });

      input.addEventListener('input', () => {
        if (input.classList.contains('gift-field__input--error')) {
          const value = input.value.trim();
          if (!value || isValidPhone(value)) {
            input.setCustomValidity('');
            input.classList.remove('gift-field__input--error');
          }
        }
      });
    });
  }

  /**
   * Wire dialog buttons (only once)
   */
  /**
   * @param {HTMLDialogElement} dialog
   */
  function wireDialogButtons(dialog) {
    if (dialog.dataset.bound === 'true') return;

    dialog.dataset.bound = 'true';
    
    // Add real-time validation
    addRealTimeValidation(dialog);
    wireLinkPreview(dialog);

    // Prevent closing the gift dialog by clicking backdrop / pressing ESC.
    // Closing it via native behavior can also accidentally close the cart drawer and leave the page "stuck".
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
    });
    dialog.addEventListener('click', (e) => {
      // In native <dialog>, clicking the backdrop often targets the dialog itself.
      if (e.target === dialog) {
        e.preventDefault();
        e.stopPropagation();
      }
    });

    // Handle cancel button - close dialog
    const cancelBtn = dialog.querySelector('[data-gift-cancel]');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', (/** @type {Event} */ e) => {
        e.preventDefault();
        e.stopPropagation();
        dialog.close('cancel');
      });
    }

    // Handle close button (X) - close dialog
    const closeBtn = dialog.querySelector('.gift-details-dialog__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (/** @type {Event} */ e) => {
        e.preventDefault();
        e.stopPropagation();
        dialog.close('cancel');
      });
    }

    // Handle save button
    const saveBtn = dialog.querySelector('[data-gift-save]');
    if (saveBtn) {
      saveBtn.addEventListener('click', async (/** @type {Event} */ e) => {
        e.preventDefault();
        e.stopPropagation();
        
        try {
          const attrs = collectFromDialog(dialog);
          
          const validation = validateAll(attrs);
          if (!validation.valid) {
            const errorMessage = validation.errors.join('. ') + '.';
            showError(dialog, errorMessage);
            return;
          }

          hideError(dialog);
          showLoading(dialog);

          await saveCartAttributes(attrs);

          hideLoading(dialog);
          dialog.close('saved');
        } catch (e) {
          console.error('Failed to save gift details:', e);
          hideLoading(dialog);
          showError(dialog, 'Could not save gift details. Please try again.');
        }
      });
    }
  }

  /**
   * Open gift details dialog for viewing/editing (without checkout requirement)
   */
  async function openGiftDetailsDialog() {
    const dialog = getDialog();
    if (!(dialog instanceof HTMLDialogElement)) {
      console.warn('Gift details dialog not found');
      return;
    }

    wireDialogButtons(dialog);
    hideError(dialog);
    try {
      dialog.showModal();
    } catch (e) {
      // Fallback: if showModal fails (e.g. due to invalid state/nesting), at least show it.
      // This won't have the native backdrop, but is better than a broken click.
      console.error('Failed to open gift details dialog with showModal():', e);
      dialog.setAttribute('open', '');
    }
  }

  /**
   * @param {Element} dialog
   * @returns {Record<string, string>}
   */
  function collectFromDialog(dialog) {
    /** @type {Record<string, string>} */
    const attrs = {};
    dialog.querySelectorAll('[data-gift-field]').forEach((/** @type {Element} */ el) => {
      const key = el.getAttribute('data-gift-field');
      if (!key) return;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        attrs[key] = (el.value || '').trim();
      }
    });

    const secret = dialog.querySelector('[data-gift-checkbox="keep_my_identity_secret"]');
    if (secret instanceof HTMLInputElement) {
      attrs.keep_my_identity_secret = secret.checked ? 'Yes' : 'No';
    }
    return attrs;
  }

  /**
   * Wire link preview for the optional media link field.
   * @param {Element} scope
   */
  function wireLinkPreview(scope) {
    const input = scope.querySelector('[data-gift-field="media_link"]');
    const preview = scope.querySelector('[data-gift-link-preview]');
    const previewA = scope.querySelector('[data-gift-link-preview-a]');
    if (!(input instanceof HTMLInputElement)) return;
    if (!(preview instanceof HTMLElement)) return;
    if (!(previewA instanceof HTMLAnchorElement)) return;

    /**
     * @param {string} value
     * @returns {string}
     */
    const normalizeUrl = (value) => {
      const raw = String(value || '').trim();
      if (!raw) return '';
      // Allow users to paste without scheme
      if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(raw)) return raw;
      return `https://${raw}`;
    };

    const update = () => {
      const url = normalizeUrl(input.value);
      if (!url) {
        preview.hidden = true;
        previewA.href = '';
        previewA.textContent = '';
        return;
      }

      preview.hidden = false;
      previewA.href = url;
      previewA.textContent = url;
    };

    input.addEventListener('input', update);
    input.addEventListener('blur', update);
    update();
  }

  /**
   * Wire inline gift details form (customize cart).
   */
  function wireInlineGiftDetails() {
    const root = getInlineGiftDetailsRoot();
    if (!root) return;
    if (root.dataset.bound === 'true') return;
    root.dataset.bound = 'true';

    addRealTimeValidation(root);
    wireLinkPreview(root);

    const saveBtn = root.querySelector('[data-gift-save]');
    if (saveBtn) {
      saveBtn.addEventListener('click', async (/** @type {Event} */ e) => {
        e.preventDefault();
        e.stopPropagation();

        try {
          const attrs = collectFromDialog(root);
          const validation = validateAll(attrs);
          if (!validation.valid) {
            const msg = validation.errors.join('. ') + '.';
            showError(root, msg);
            root.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            return;
          }

          hideError(root);
          showLoading(root);
          await saveCartAttributes(attrs);
          hideLoading(root);
          showNotification('Gift details saved.', 'success', saveBtn);
        } catch (err) {
          console.error('Failed to save inline gift details:', err);
          hideLoading(root);
          showError(root, 'Could not save gift details. Please try again.');
        }
      });
    }
  }

  /**
   * Validate email format
   * @param {string} email
   * @returns {boolean}
   */
  function isValidEmail(email) {
    if (!email || !email.trim()) return true; // Optional field
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  }

  /**
   * Clean phone number (remove spaces, dashes, parentheses)
   * @param {string} phone
   * @returns {string}
   */
  function cleanPhoneNumber(phone) {
    if (!phone) return '';
    return phone.trim().replace(/[\s\-\(\)]/g, '');
  }

  /**
   * Validate phone number format (must start with + and country code)
   * @param {string} phone
   * @returns {boolean}
   */
  function isValidPhone(phone) {
    if (!phone || !phone.trim()) return true; // Optional field
    // Must start with + followed by 1-15 digits (E.164 format)
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    // Remove spaces, dashes, parentheses for validation
    const cleaned = cleanPhoneNumber(phone);
    return phoneRegex.test(cleaned);
  }

  /**
   * Get the currently "active" delivery-scheduling element.
   * The theme may render delivery scheduling in multiple contexts (e.g. cart drawer + cart page).
   * We prefer the one inside the open cart drawer; otherwise prefer the one in main content.
   * @returns {Element|null}
   */
  function getActiveDeliverySchedulingEl() {
    const openDrawerDialog = document.querySelector('cart-drawer-component dialog[open]');
    if (openDrawerDialog instanceof HTMLDialogElement) {
      const inDrawer = openDrawerDialog.querySelector('delivery-scheduling');
      if (inDrawer) return inDrawer;
    }

    const main = document.querySelector('main#MainContent');
    if (main instanceof HTMLElement) {
      const inMain = main.querySelector('delivery-scheduling');
      if (inMain) return inMain;
    }

    return document.querySelector('delivery-scheduling');
  }

  function getPersistedDeliverySchedulingState() {
    try {
      const raw = sessionStorage.getItem('ccw-delivery-scheduling-state');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Validate delivery scheduling (required for all orders)
   * @param {Record<string, string>} attrs
   * @returns {{valid: boolean, errors: string[]}}
   */
  function validateDeliveryScheduling(attrs) {
    // If delivery scheduling UI isn't enabled/rendered, do not validate.
    // (When the setting is OFF, `delivery-scheduling` won't exist in the DOM.)
    const schedulingEl = getActiveDeliverySchedulingEl();
    if (!schedulingEl) {
      return { valid: true, errors: [] };
    }

    const errors = [];

    // Delivery date is always required.
    // Prefer reading the current input value so clearing the field blocks checkout immediately
    // even if cart attributes still have an old saved value.
    const persistedState = getPersistedDeliverySchedulingState();
    const persistedDate = String(persistedState?.delivery_date || '').trim();
    const savedDate = String(attrs.delivery_date || '').trim() || persistedDate;
    const dateInput = schedulingEl.querySelector('#delivery-date');
    let dateValue = savedDate;

    if (dateInput instanceof HTMLInputElement) {
      const inputValue = String(dateInput.value || '').trim();
      dateValue = inputValue || savedDate;

      // If the cart already has a saved date, hydrate the visible field as a safeguard.
      if (!inputValue && savedDate) {
        dateInput.value = savedDate;
      }
    }

    if (!dateValue) {
      errors.push('Delivery date is required');
    }

    const schedulingWithValidation = /** @type {any} */ (schedulingEl);
    if (errors.length === 0 && dateInput instanceof HTMLInputElement && typeof schedulingWithValidation?.validateDateInput === 'function') {
      const validation = schedulingWithValidation.validateDateInput({ report: false });
      if (!validation.valid) {
        errors.push(validation.message || 'Selected delivery date is unavailable');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate all fields including format validation
   * @param {Record<string, string>} attrs
   * @returns {{valid: boolean, errors: string[]}}
   */
  /**
   * @param {Record<string, string>} attrs
   * @returns {{valid: boolean, errors: string[]}}
   */
  function validateAll(attrs) {
    const errors = [];

    // Required receiver fields
    if (!String(attrs.receiver_name || '').trim()) {
      errors.push('Receiver name is required');
    }

    if (!String(attrs.receiver_phone || '').trim()) {
      errors.push('Receiver phone number is required');
    } else if (!isValidPhone(String(attrs.receiver_phone || ''))) {
      errors.push('Receiver phone number must start with a country code (e.g., +961)');
    }

    if (!String(attrs.receiver_address || '').trim()) {
      errors.push('Receiver delivery address is required');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Customize cart wizard presence (page.customize-cart).
   * @returns {HTMLElement|null}
   */
  function getCustomizeCartWizardRoot() {
    const el = document.querySelector('[data-ccw-root]');
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * Read current form values from the wizard DOM (so we validate inputs, not just saved cart attrs).
   * @param {HTMLElement} root
   * @returns {Record<string, string>}
   */
  function getFieldsFromWizardRoot(root) {
    /** @type {Record<string, string>} */
    const attrs = {};
    root.querySelectorAll('[data-ccw-field]').forEach((el) => {
      const key = el.getAttribute('data-ccw-field');
      if (!key) return;
      if (el instanceof HTMLInputElement) {
        if (el.type === 'checkbox') {
          attrs[key] = el.checked ? 'Yes' : 'No';
        } else {
          attrs[key] = String(el.value || '').trim();
        }
        return;
      }
      if (el instanceof HTMLSelectElement) {
        attrs[key] = String(el.value || '').trim();
        return;
      }
      if (el instanceof HTMLTextAreaElement) {
        attrs[key] = String(el.value || '').trim();
      }
    });
    root.querySelectorAll('[data-ccw-phone-number]').forEach((el) => {
      if (!(el instanceof HTMLInputElement)) return;
      const key = el.getAttribute('data-ccw-phone-number');
      if (!key) return;
      const prefixEl = root.querySelector(`[data-ccw-phone-prefix="${key}"]`);
      const prefix = prefixEl instanceof HTMLSelectElement ? String(prefixEl.value || '').trim() : '';
      const rest = cleanPhoneNumber(String(el.value || '').trim()).replace(/^\+/, '');
      attrs[key] = prefix && rest ? `${prefix}${rest}` : '';
    });
    return attrs;
  }

  /**
   * Clear inline field errors inside the wizard.
   * @param {HTMLElement} wizardRoot
   */
  function clearWizardFieldErrors(wizardRoot) {
    wizardRoot.querySelectorAll('[data-ccw-field-error]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.hidden = true;
      el.textContent = '';
    });
    wizardRoot.querySelectorAll('.ccw__invalid').forEach((el) => el.classList.remove('ccw__invalid'));

    // Clear panel-level error box if present
    wizardRoot.querySelectorAll('[data-ccw-error]').forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = true;
    });
  }

  /**
   * Apply field errors to the wizard UI (under inputs + red border).
   * @param {HTMLElement} wizardRoot
   * @param {'customize'|'delivery'} step
   * @param {Record<string, string>} fieldErrors
   */
  function applyWizardFieldErrors(wizardRoot, step, fieldErrors) {
    clearWizardFieldErrors(wizardRoot);

    const panel = wizardRoot.querySelector(`[data-ccw-panel="${step}"]`);
    const scope = panel instanceof HTMLElement ? panel : wizardRoot;

    Object.entries(fieldErrors).forEach(([key, msg]) => {
      const err = scope.querySelector(`[data-ccw-field-error="${key}"]`);
      if (err instanceof HTMLElement) {
        err.textContent = msg;
        err.hidden = false;
      }
      const input =
        scope.querySelector(`[data-ccw-field="${key}"]`) ||
        scope.querySelector(`[data-ccw-phone-number="${key}"]`);
      if (input instanceof HTMLElement) input.classList.add('ccw__invalid');
    });

    const firstKey = Object.keys(fieldErrors)[0];
    if (firstKey) {
      const first =
        scope.querySelector(`[data-ccw-field="${firstKey}"]`) ||
        scope.querySelector(`[data-ccw-phone-number="${firstKey}"]`);
      if (first instanceof HTMLElement) {
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (first instanceof HTMLInputElement || first instanceof HTMLTextAreaElement || first instanceof HTMLSelectElement) {
          first.focus();
        }
      }
    }
  }

  /**
   * Validate customize cart wizard-required fields (matches wizard: delivery first, then greeting).
   * @param {Record<string, string>} attrs
   * @returns {{valid:boolean, errors:string[], fieldErrors: Record<string,string>, step:'customize'|'delivery'}}
   */
  function validateCustomizeCartWizard(attrs) {
    /** @type {Record<string, string>} */
    const fieldErrors = {};

    // Delivery step (step 1): recipient + sender
    const recipientFullName = String(attrs.recipient_full_name || '').trim();
    const recipientPhone = String(attrs.recipient_phone || '').trim();
    const recipientDistrict = String(attrs.recipient_district || '').trim();
    const recipientDetails = String(attrs.recipient_delivery_details || '').trim();

    if (!recipientFullName) fieldErrors.recipient_full_name = 'Full name is required';
    if (!recipientPhone) fieldErrors.recipient_phone = 'Phone is required';
    else if (!isValidPhone(recipientPhone)) fieldErrors.recipient_phone = 'Please enter a valid phone number';
    if (!recipientDistrict) fieldErrors.recipient_district = 'District is required';
    if (!recipientDetails) fieldErrors.recipient_delivery_details = 'Delivery details is required';

    const senderFullName = String(attrs.sender_full_name || '').trim();
    const senderWhatsApp = String(attrs.sender_whatsapp || '').trim();
    const senderEmail = String(attrs.sender_email || '').trim();

    if (!senderFullName) fieldErrors.sender_full_name = 'Full name is required';
    if (!senderWhatsApp) fieldErrors.sender_whatsapp = 'Phone number is required';
    else if (!isValidPhone(senderWhatsApp)) fieldErrors.sender_whatsapp = 'Please enter a valid phone number';
    if (!senderEmail) fieldErrors.sender_email = 'Email is required';
    else if (!isValidEmail(senderEmail)) fieldErrors.sender_email = 'Please enter a valid email address';

    // Step to show: first failing step (delivery then customize)
    const step =
      Object.prototype.hasOwnProperty.call(fieldErrors, 'recipient_full_name') ||
      Object.prototype.hasOwnProperty.call(fieldErrors, 'recipient_phone') ||
      Object.prototype.hasOwnProperty.call(fieldErrors, 'recipient_district') ||
      Object.prototype.hasOwnProperty.call(fieldErrors, 'recipient_delivery_details') ||
      Object.prototype.hasOwnProperty.call(fieldErrors, 'sender_full_name') ||
      Object.prototype.hasOwnProperty.call(fieldErrors, 'sender_whatsapp') ||
      Object.prototype.hasOwnProperty.call(fieldErrors, 'card_from') ||
      Object.prototype.hasOwnProperty.call(fieldErrors, 'sender_email')
        ? 'delivery'
        : 'customize';

    const errors = Object.values(fieldErrors);
    return { valid: errors.length === 0, errors, fieldErrors, step };
  }

  /**
   * @param {Record<string, string>} attrs
   */
  function validateRequired(attrs) {
    const validation = validateAll(attrs);
    return validation.valid;
  }

  /**
   * @param {Record<string, string>} attrs
   */
  async function saveCartAttributes(attrs) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: attrs }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`cart/update.js failed: ${res.status} ${text}`);
    }
    return res.json();
  }

  /**
   * @param {Element} dialog
   * @param {string} msg
   */
  function showError(dialog, msg) {
    const el = dialog.querySelector('.gift-details-dialog__error');
    if (el instanceof HTMLElement) {
      const errorText = el.querySelector('.gift-details-dialog__error-text');
      if (errorText) {
        errorText.textContent = msg;
      }
      el.hidden = false;
    }
  }

  /**
   * @param {Element} dialog
   */
  function hideError(dialog) {
    const el = dialog.querySelector('.gift-details-dialog__error');
    if (el instanceof HTMLElement) el.hidden = true;
  }

  /**
   * Show notification message in cart
   * @param {string} message - The message to display
   * @param {string} type - The notification type ('error', 'success')
   * @param {Element | null} [contextEl] - Element near where to render (checkout button/link)
   */
  const notificationHideTimers = new WeakMap();

  function ensureNotificationStyles() {
    if (document.getElementById('cart-validation-notification-styles')) return;
    const style = document.createElement('style');
    style.id = 'cart-validation-notification-styles';
    style.textContent = `
      .cart__notification[hidden]{display:none!important;}
      .cart__notification{
        display:flex;align-items:flex-start;justify-content:space-between;gap:12px;
        padding:12px 14px;margin:0 0 12px;
        border-radius:var(--style-border-radius-inputs,10px);
        border:1px solid var(--color-border,#ddd);
        background:rgb(var(--color-foreground-rgb,0 0 0)/0.03);
        color:var(--color-foreground,#111);
        font-size:14px;line-height:1.4;
      }
      .cart__notification--error{
        border-color:var(--color-error,#d32f2f);
        background:rgb(211 47 47/0.08);
        color:var(--color-error,#d32f2f);
      }
      .cart__notification--success{
        border-color:var(--color-success,#2e7d32);
        background:rgb(46 125 50/0.08);
        color:var(--color-success,#2e7d32);
      }
      .cart__notification-text{flex:1;min-width:0;}
      .cart__notification-close{
        flex:0 0 auto;
        width:28px;height:28px;line-height:28px;
        border:0;background:transparent;color:inherit;
        opacity:.75;cursor:pointer;border-radius:999px;
        display:flex;align-items:center;justify-content:center;
      }
      .cart__notification-close:hover{opacity:1;background:rgb(0 0 0/0.06);}
    `;
    document.head.appendChild(style);
  }

  /**
   * @param {Element | null} contextEl
   * @returns {HTMLElement | null}
   */
  function getOrCreateNotification(contextEl) {
    ensureNotificationStyles();

    /** @type {HTMLElement | null} */
    let root = null;

    if (contextEl && contextEl.closest) {
      root = /** @type {HTMLElement|null} */ (contextEl.closest('.cart__ctas'));
    }
    if (!root) {
      root = document.querySelector('.cart__ctas');
    }
    if (!root) return null;

    let notification = /** @type {HTMLElement|null} */ (root.querySelector('[data-cart-notification]'));
    if (notification) return notification;

    notification = document.createElement('div');
    notification.className = 'cart__notification cart__notification--error';
    notification.setAttribute('data-cart-notification', 'true');
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'assertive');
    notification.hidden = true;
    notification.innerHTML = `
      <div class="cart__notification-text"></div>
      <button type="button" class="cart__notification-close" aria-label="Close">×</button>
    `;

    const closeBtn = notification.querySelector('.cart__notification-close');
    closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      notification.hidden = true;
    });

    // Put it above the checkout button (first child inside CTAs)
    root.insertBefore(notification, root.firstChild);
    return notification;
  }

  /**
   * @param {string} message
   * @param {'error'|'success'} [type]
   * @param {Element | null} [contextEl]
   */
  function showNotification(message, type = 'error', contextEl = null) {
    const notification = getOrCreateNotification(contextEl);
    if (!notification) return;

    const notificationText = notification.querySelector('.cart__notification-text');
    if (notificationText) notificationText.textContent = message;

    notification.className = `cart__notification cart__notification--${type}`;
    notification.hidden = false;

    // Clear previous timer (if any)
    const prevTimer = notificationHideTimers.get(notification);
    if (prevTimer) clearTimeout(prevTimer);

    const timer = setTimeout(() => {
      notification.hidden = true;
    }, 8000);
    notificationHideTimers.set(notification, timer);

    notification.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * @param {string | (() => void)} targetCheckoutUrlOrSubmitFn
   */
  async function openPopupAndResolveCheckout(targetCheckoutUrlOrSubmitFn) {
    // Popup removed: redirect to the wizard instead.
    // Keep this function so older code paths can't accidentally hard-fail checkout.
    // eslint-disable-next-line no-unused-vars
    const _unused = targetCheckoutUrlOrSubmitFn;
    if (REDIRECT_DRAWER_TO_CUSTOMIZE_CART) {
      closeOpenCartDrawer();
      window.location.href = CUSTOMIZE_CART_URL;
    }
  }

  function interceptCheckoutClicks() {
    // Cover both:
    // - Cart drawer: <a href="/checkout"> OR button with id="checkout"/name="checkout"
    // - Cart page: same patterns
    // Note: There's no form with action="/checkout" - buttons redirect directly to /checkout

    document.addEventListener(
      'click',
      async (/** @type {MouseEvent} */ e) => {
        const target = e.target instanceof Element ? e.target : null;
        if (!target) return;

        // Find the closest checkout link or checkout button
        // Button can have id="checkout", name="checkout", or be a submit button
        /** @type {HTMLAnchorElement|null} */
        let checkoutLink = null;
        const maybeAnchor = target.closest('a[href]');
        if (maybeAnchor instanceof HTMLAnchorElement && maybeAnchor.href) {
          try {
            const u = new URL(maybeAnchor.href, window.location.origin);
            if (u.pathname.startsWith('/checkout')) checkoutLink = maybeAnchor;
          } catch (_) {
            // ignore malformed href
          }
        }

        // Support common theme patterns:
        // - id="checkout"
        // - name="checkout"
        // - submit button tied to cart form via form="cart-form"
        const checkoutBtn = target.closest(
          '#checkout, button[name="checkout"], input[name="checkout"], button[type="submit"][form="cart-form"], input[type="submit"][form="cart-form"]'
        );
        
        const isCheckoutClick = Boolean(checkoutLink || checkoutBtn);
        if (!isCheckoutClick) return;

        // Block immediately (async work below). We'll resume manually if allowed.
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

        const resume = () => proceedToCheckout(checkoutLink, checkoutBtn);

        await flushActiveDeliverySchedulingSave();

        // Get cart and attributes
        const cart = await (
          await fetch('/cart.js', {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
          })
        ).json();
        const attrs = cart.attributes || {};

        // Validate delivery scheduling for ALL orders (priority requirement)
        const deliveryValidation = validateDeliveryScheduling({
          delivery_date: attrs.delivery_date || '',
        });

        if (!deliveryValidation.valid) {
          // Show notification with delivery scheduling errors
          const errorMessage = deliveryValidation.errors.length > 0 
            ? deliveryValidation.errors.join('. ') + '. Please select a delivery date before checkout.'
            : 'Please select a delivery date before checkout.';
          
          showNotification(errorMessage, 'error', checkoutBtn || checkoutLink);
          
          // Scroll to delivery scheduling section
          const deliverySection = getActiveDeliverySchedulingEl();
          if (deliverySection) {
            const details = deliverySection.querySelector('details');
            if (details && !details.open) {
              details.open = true;
            }
            const dateInput = deliverySection.querySelector('#delivery-date');
            if (dateInput instanceof HTMLInputElement) {
              dateInput.classList.add('delivery-scheduling__input--error');
              dateInput.focus();
            }
            deliverySection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
          
          return;
        }

        // Check if cart has gift-card products
        const hasGiftCard = await hasGiftCardProduct();
        if (hasGiftCard) {
          const wizardRoot = getCustomizeCartWizardRoot();

          // Validate current inputs when wizard is on page; otherwise validate saved cart attributes.
          const attrsToValidate = wizardRoot ? getFieldsFromWizardRoot(wizardRoot) : attrs;
          /** @type {{valid:boolean, errors:string[], fieldErrors: Record<string,string>, step?: 'customize'|'delivery'}} */
          const giftValidation = validateCustomizeCartWizard(attrsToValidate);

          if (!giftValidation.valid) {
            const errorMessage = giftValidation.errors.length > 0
              ? giftValidation.errors.join('. ') + '. Please complete Customize and Delivery Details before checkout.'
              : 'Please complete Customize and Delivery Details before checkout.';
            
            showNotification(errorMessage, 'error', checkoutBtn || checkoutLink);

            // On customize cart we use a wizard; switch to the failing step and scroll to it.
            if (wizardRoot) {
              const step = giftValidation.step || 'customize';
              const tabBtn = wizardRoot.querySelector(`[data-ccw-tab="${step}"]`);
              if (tabBtn instanceof HTMLElement) tabBtn.click();

              const panel = wizardRoot.querySelector(`[data-ccw-panel="${step}"]`);
              if (panel instanceof HTMLElement) {
                // Apply inline errors under each field (same UX as Next button)
                applyWizardFieldErrors(wizardRoot, step, giftValidation.fieldErrors || {});
                panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } else {
                wizardRoot.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
              return;
            }

            // Otherwise redirect user to the wizard page.
            if (REDIRECT_DRAWER_TO_CUSTOMIZE_CART) {
              closeOpenCartDrawer();
              window.location.href = CUSTOMIZE_CART_URL;
            }
            return;
          }
        }

        // All validations passed, proceed to checkout
        resume();
      },
      true // capture to beat theme handlers
    );
  }

  // Init once
  wireInlineGiftDetails();
  interceptCheckoutClicks();
})();
